import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useStore } from "../store";
import { useFmeas, usePatchFmea } from "../api/sitroom";

const inputStyle = {
  background: "#0d0f18",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#f0f2f8",
  fontFamily: "'DM Mono', monospace",
  fontSize: 11,
  width: "100%",
  borderRadius: 4,
  padding: "8px 10px",
};

const Label = ({ children }) => (
  <label
    style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: 9,
      letterSpacing: "1.4px",
      color: "#9198b0",
      textTransform: "uppercase",
    }}
  >
    {children}
  </label>
);

const isoDate = (d) => d.toISOString().slice(0, 10);
const plus14 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return isoDate(d);
};

const randomTicket = (prefix) => Math.floor(100 + Math.random() * 900);

const rpnToPriority = (rpn) => (rpn >= 200 ? "High" : rpn >= 100 ? "Medium" : "Low");

const accentMap = {
  jira: { color: "#0052CC", label: "Jira", btn: "Create Ticket" },
  planner: { color: "#7B5EA7", label: "MS Planner", btn: "Create Task" },
};

export const IntegrationModal = () => {
  const integrationModal = useStore((s) => s.integrationModal);
  const closeIntegrationModal = useStore((s) => s.closeIntegrationModal);
  const addToast = useStore((s) => s.addToast);
  const { data: list = [] } = useFmeas();
  const patchMutation = usePatchFmea();
  const updateFmea = (id, patch) => patchMutation.mutate({ id, patch });

  const fmea = list.find((f) => f.id === integrationModal.fmeaId);

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!integrationModal.open || !fmea) {
      setForm(null);
      return;
    }
    const rpn = fmea.severity * fmea.occurrence * fmea.detection;
    if (integrationModal.type === "jira") {
      setForm({
        project: "SITROOM-QA",
        issueType: "Improvement",
        summary: `FMEA Corrective Action — ${fmea.failureMode}`,
        description: `${fmea.recommendedAction}\n\nSource: ${fmea.sourceIncident}\nRPN: ${rpn}`,
        priority: rpnToPriority(rpn),
        assignee: "",
        dueDate: plus14(),
      });
    } else if (integrationModal.type === "planner") {
      setForm({
        plan: "SITROOM Quality Actions",
        bucket: "To Do",
        taskTitle: fmea.failureMode,
        notes: fmea.recommendedAction,
        assignedTo: "",
        dueDate: plus14(),
      });
    }
  }, [integrationModal, fmea]);

  if (!integrationModal.open || !fmea || !form) return null;

  const accent = accentMap[integrationModal.type];
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (integrationModal.type === "jira") {
      const ticketId = `SITROOM-QA-${randomTicket()}`;
      updateFmea(fmea.id, {
        jira: { ticketId, project: form.project, priority: form.priority },
      });
      addToast({
        type: "info",
        title: "Jira ticket created",
        body: ticketId,
      });
    } else {
      const taskId = `PLAN-${randomTicket()}`;
      updateFmea(fmea.id, {
        planner: { taskId, plan: form.plan, taskTitle: form.taskTitle },
      });
      addToast({
        type: "info",
        title: "MS Planner task created",
        body: form.taskTitle,
      });
    }
    closeIntegrationModal();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[700] backdrop-enter"
        style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        onClick={closeIntegrationModal}
        data-testid="integration-modal-backdrop"
      />
      <div
        className="fixed z-[701] flex items-end sm:items-center justify-center pointer-events-none"
        style={{ inset: 0 }}
      >
        <div
          className="modal-enter pointer-events-auto flex flex-col"
          style={{
            background: "#111420",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "12px",
            width: "min(520px, 100vw)",
            maxHeight: "90dvh",
            boxShadow: "0 30px 70px rgba(0,0,0,0.6)",
          }}
          data-testid={`integration-modal-${integrationModal.type}`}
        >
          <header
            className="relative px-5 py-4"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}
          >
            <div className="flex items-center gap-2.5">
              <span
                className="inline-block rounded-md"
                style={{
                  width: 22,
                  height: 22,
                  background: `${accent.color}22`,
                  border: `1px solid ${accent.color}80`,
                  color: accent.color,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  textAlign: "center",
                  lineHeight: "22px",
                }}
              >
                {integrationModal.type === "jira" ? "J" : "P"}
              </span>
              <div
                style={{
                  fontFamily: "'Syne', system-ui, sans-serif",
                  fontWeight: 800,
                  fontSize: 16,
                  color: "#f0f2f8",
                }}
              >
                Create {accent.label} {integrationModal.type === "jira" ? "ticket" : "task"}
              </div>
            </div>
            <div
              className="mt-1"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9198b0",
                letterSpacing: "0.5px",
              }}
            >
              From {fmea.id} · {fmea.sourceIncident}
            </div>
            <button
              onClick={closeIntegrationModal}
              className="absolute right-3 top-3 flex items-center justify-center rounded-md hover:bg-white/5"
              style={{
                width: 28,
                height: 28,
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#9aa1b3",
              }}
              data-testid="close-integration-modal-btn"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {integrationModal.type === "jira" ? (
              <>
                <div>
                  <Label>Project</Label>
                  <input
                    style={inputStyle}
                    className="mt-1"
                    value={form.project}
                    onChange={(e) => setField("project", e.target.value)}
                    data-testid="jira-project-input"
                  />
                </div>
                <div>
                  <Label>Issue Type</Label>
                  <select
                    style={inputStyle}
                    className="mt-1"
                    value={form.issueType}
                    onChange={(e) => setField("issueType", e.target.value)}
                    data-testid="jira-type-select"
                  >
                    <option style={{ background: "#0d0f18" }}>Task</option>
                    <option style={{ background: "#0d0f18" }}>Bug</option>
                    <option style={{ background: "#0d0f18" }}>Improvement</option>
                  </select>
                </div>
                <div>
                  <Label>Summary</Label>
                  <input
                    style={inputStyle}
                    className="mt-1"
                    value={form.summary}
                    onChange={(e) => setField("summary", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <textarea
                    rows={4}
                    style={{ ...inputStyle, resize: "vertical" }}
                    className="mt-1"
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Priority</Label>
                    <select
                      style={inputStyle}
                      className="mt-1"
                      value={form.priority}
                      onChange={(e) => setField("priority", e.target.value)}
                    >
                      <option style={{ background: "#0d0f18" }}>High</option>
                      <option style={{ background: "#0d0f18" }}>Medium</option>
                      <option style={{ background: "#0d0f18" }}>Low</option>
                    </select>
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <input
                      type="date"
                      style={inputStyle}
                      className="mt-1"
                      value={form.dueDate}
                      onChange={(e) => setField("dueDate", e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Assignee</Label>
                  <input
                    style={inputStyle}
                    className="mt-1"
                    placeholder="email or username"
                    value={form.assignee}
                    onChange={(e) => setField("assignee", e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Plan</Label>
                  <input
                    style={inputStyle}
                    className="mt-1"
                    value={form.plan}
                    onChange={(e) => setField("plan", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Bucket</Label>
                  <select
                    style={inputStyle}
                    className="mt-1"
                    value={form.bucket}
                    onChange={(e) => setField("bucket", e.target.value)}
                    data-testid="planner-bucket-select"
                  >
                    <option style={{ background: "#0d0f18" }}>To Do</option>
                    <option style={{ background: "#0d0f18" }}>In Progress</option>
                    <option style={{ background: "#0d0f18" }}>Completed</option>
                  </select>
                </div>
                <div>
                  <Label>Task Title</Label>
                  <input
                    style={inputStyle}
                    className="mt-1"
                    value={form.taskTitle}
                    onChange={(e) => setField("taskTitle", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <textarea
                    rows={4}
                    style={{ ...inputStyle, resize: "vertical" }}
                    className="mt-1"
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Assigned To</Label>
                    <input
                      style={inputStyle}
                      className="mt-1"
                      placeholder="email or name"
                      value={form.assignedTo}
                      onChange={(e) => setField("assignedTo", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <input
                      type="date"
                      style={inputStyle}
                      className="mt-1"
                      value={form.dueDate}
                      onChange={(e) => setField("dueDate", e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <footer
            className="flex items-center gap-2 px-5 py-3"
            style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}
          >
            <button
              onClick={closeIntegrationModal}
              className="px-4 py-2 rounded-md"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#9aa1b3",
                fontFamily: "'Syne', system-ui, sans-serif",
                fontWeight: 600,
                fontSize: 12,
              }}
              data-testid="cancel-integration-btn"
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              onClick={handleSubmit}
              className="px-4 py-2 rounded-md"
              style={{
                background: accent.color,
                color: "#fff",
                fontFamily: "'Syne', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: "0.4px",
              }}
              data-testid="submit-integration-btn"
            >
              {accent.btn}
            </button>
          </footer>
        </div>
      </div>
    </>
  );
};
